import React, { useCallback, useEffect, useState } from "react";
import {
  Alert,
  Button,
  Form,
  ListGroup,
  Modal,
  Spinner,
  Table,
} from "react-bootstrap";
import {
  FaWpforms,
  FaUsers,
  FaEdit,
  FaUserTag,
  FaUserShield,
  FaUserMinus,
  FaCheck,
  FaWhatsapp,
  FaCalendarDay,
  FaTable,
} from "react-icons/fa";
import api from "../api";
import ProjectFormManager from "./ProjectFormManager";
import {
  formatSouthAfricaDateTime,
  SOUTH_AFRICA_TIME_LABEL,
} from "../utils/projectFormDateTime";

const TAB_DETAILS = "details";
const TAB_FORMS = "forms";
const TAB_MEMBERS = "members";

/* ─── Build slot → students roster from form definition + responses ─────────── */
const buildRoster = (form, responses) => {
  const roster = {};
  for (const field of form.fields.filter((f) => f.type === "availability")) {
    for (const slot of field.slots || []) {
      roster[String(slot._id)] = {
        slot,
        fieldLabel: field.label,
        date: new Date(slot.startTime),
        students: [],
      };
    }
  }
  for (const resp of responses) {
    for (const answer of resp.answers || []) {
      if (!Array.isArray(answer.value)) continue;
      for (const slotId of answer.value) {
        if (roster[String(slotId)]) {
          roster[String(slotId)].students.push(resp.respondent);
        }
      }
    }
  }
  const items = Object.values(roster).sort((a, b) => a.date - b.date);
  const groups = {};
  for (const item of items) {
    const key = item.date.toLocaleDateString("en-ZA", {
      weekday: "long",
      day: "numeric",
      month: "long",
      year: "numeric",
    });
    (groups[key] ||= []).push(item);
  }
  return groups;
};

/* ─── AvailabilityRoster component ──────────────────────────────────────────── */
const AvailabilityRoster = ({ form, responses }) => {
  const grouped = buildRoster(form, responses);
  const days = Object.keys(grouped);

  if (days.length === 0) {
    return (
      <Alert variant="info" style={{ fontSize: "0.85rem" }}>
        This form has no availability slots defined.
      </Alert>
    );
  }

  return (
    <div className="d-flex flex-column gap-3">
      {days.map((day) => (
        <div
          key={day}
          style={{
            border: "1px solid var(--color-border)",
            borderRadius: "var(--radius-md)",
            overflow: "hidden",
          }}>
          <div
            className="d-flex align-items-center gap-2 px-3 py-2"
            style={{
              backgroundColor: "rgba(27,58,135,0.06)",
              borderBottom: "1px solid var(--color-border)",
            }}>
            <FaCalendarDay
              size={13}
              style={{ color: "var(--color-primary)" }}
            />
            <strong
              style={{ color: "var(--color-primary)", fontSize: "0.9rem" }}>
              {day}
            </strong>
          </div>
          <div className="d-flex flex-column">
            {grouped[day].map((item, idx) => {
              const { slot, students } = item;
              const start = formatSouthAfricaDateTime(slot.startTime, "HH:mm");
              const end = formatSouthAfricaDateTime(slot.endTime, "HH:mm");
              return (
                <div
                  key={String(slot._id)}
                  className="px-3 py-2"
                  style={{
                    borderTop:
                      idx > 0 ? "1px solid var(--color-border)" : "none",
                    backgroundColor:
                      students.length > 0
                        ? "rgba(0,172,150,0.03)"
                        : "transparent",
                  }}>
                  <div className="d-flex align-items-baseline gap-2 mb-1 flex-wrap">
                    <span
                      style={{
                        fontSize: "0.78rem",
                        fontWeight: 700,
                        color: "var(--color-teal)",
                        whiteSpace: "nowrap",
                      }}>
                      {start} – {end} {SOUTH_AFRICA_TIME_LABEL}
                    </span>
                    <span
                      style={{
                        fontSize: "0.85rem",
                        fontWeight: 600,
                        color: "var(--color-text)",
                      }}>
                      {slot.label || slot.activity}
                    </span>
                    {slot.location && (
                      <span
                        style={{
                          fontSize: "0.72rem",
                          color: "var(--color-text-secondary)",
                        }}>
                        · {slot.location}
                      </span>
                    )}
                  </div>
                  {students.length > 0 ? (
                    <div className="d-flex flex-wrap gap-1 align-items-center">
                      {students.map((student, sIdx) => {
                        if (!student) return null;
                        const name =
                          `${student.name || ""} ${student.surname || ""}`.trim() ||
                          "Unknown";
                        return (
                          <span
                            key={student._id || sIdx}
                            title={student.studentNumber || name}
                            style={{
                              display: "inline-flex",
                              alignItems: "center",
                              gap: "5px",
                              padding: "2px 10px",
                              borderRadius: "20px",
                              backgroundColor: "rgba(0,172,150,0.12)",
                              color: "var(--color-teal)",
                              fontSize: "0.75rem",
                              fontWeight: 600,
                              border: "1px solid rgba(0,172,150,0.25)",
                            }}>
                            <span
                              style={{
                                width: "18px",
                                height: "18px",
                                borderRadius: "50%",
                                backgroundColor: "var(--color-teal)",
                                color: "#fff",
                                display: "inline-flex",
                                alignItems: "center",
                                justifyContent: "center",
                                fontSize: "0.6rem",
                                fontWeight: 700,
                                flexShrink: 0,
                              }}>
                              {name.charAt(0).toUpperCase()}
                            </span>
                            {name}
                            {student.studentNumber && (
                              <span style={{ opacity: 0.65, fontWeight: 400 }}>
                                ({student.studentNumber})
                              </span>
                            )}
                          </span>
                        );
                      })}
                      <span
                        style={{
                          fontSize: "0.72rem",
                          color: "var(--color-text-secondary)",
                          marginLeft: "2px",
                        }}>
                        — {students.length} available
                      </span>
                    </div>
                  ) : (
                    <span
                      style={{
                        fontSize: "0.75rem",
                        color: "var(--color-text-secondary)",
                        fontStyle: "italic",
                      }}>
                      No one available yet
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}
    </div>
  );
};

/* ─── Main component ─────────────────────────────────────────────────────────── */
const ProjectLeaderManagementModal = ({ show, onHide, project, onUpdated }) => {
  const [activeTab, setActiveTab] = useState(TAB_DETAILS);

  // Details tab
  const [members, setMembers] = useState([]);
  const [requests, setRequests] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({});

  // Forms tab
  const [forms, setForms] = useState([]);
  const [formsLoading, setFormsLoading] = useState(false);
  const [formsError, setFormsError] = useState(null);
  const [showFormBuilder, setShowFormBuilder] = useState(false);
  const [activeResponseForm, setActiveResponseForm] = useState(null);
  const [formResponses, setFormResponses] = useState([]);
  const [responsesLoading, setResponsesLoading] = useState(false);
  const [responsesError, setResponsesError] = useState(null);
  const [responseView, setResponseView] = useState("roster");

  // Assign modal
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [assignForm, setAssignForm] = useState(null);
  const [assignRespondentId, setAssignRespondentId] = useState("");
  const [assignSlots, setAssignSlots] = useState({});
  const [assignSaving, setAssignSaving] = useState(false);
  const [assignError, setAssignError] = useState("");
  const [assignSuccess, setAssignSuccess] = useState("");

  useEffect(() => {
    if (!show || !project) return;
    setActiveTab(TAB_DETAILS);
    setForm({
      description: project.description || "",
      role: project.participantTypes?.[0]?.role || "",
      additionalRequirements:
        project.participantTypes?.[0]?.additionalRequirements || "",
      minimum: project.weeklyHoursRequired?.minimum ?? 0,
      maximum: project.weeklyHoursRequired?.maximum ?? 0,
      transportProvided: Boolean(project.location?.transportProvided),
      transportDetails: project.location?.transportDetails || "",
      whatsappGroup: project.communicationLinks?.whatsappGroup || "",
      whatsappChannel: project.communicationLinks?.whatsappChannel || "",
      visibility: project.visibility || "public",
    });
    setMessage(null);
    setError(null);
    setActiveResponseForm(null);
    setLoading(true);
    Promise.all([
      api.get(`/projects/${project._id}/participants`),
      api.get(`/participations/${project._id}/join-requests`),
    ])
      .then(([mr, rr]) => {
        setMembers(mr.data || []);
        setRequests(rr.data || []);
      })
      .catch((e) =>
        setError(
          e.response?.data?.error || "Unable to load project management data.",
        ),
      )
      .finally(() => setLoading(false));
  }, [show, project]);

  const loadForms = useCallback(async () => {
    if (!project?._id) return;
    setFormsLoading(true);
    setFormsError(null);
    try {
      const res = await api.get(`/projects/${project._id}/forms`);
      setForms(res.data || []);
    } catch (e) {
      setFormsError(e.response?.data?.error || "Failed to load forms.");
    } finally {
      setFormsLoading(false);
    }
  }, [project]);

  useEffect(() => {
    if (activeTab === TAB_FORMS && show) loadForms();
  }, [activeTab, show, loadForms]);

  const submitAmendment = async (event) => {
    event.preventDefault();
    setSaving(true);
    setError(null);
    try {
      const location = project.location
        ? {
            ...project.location,
            transportProvided: form.transportProvided,
            transportDetails: form.transportProvided
              ? form.transportDetails
              : "",
          }
        : project.location;
      const res = await api.patch(`/projects/${project._id}`, {
        description: form.description,
        weeklyHoursRequired: {
          minimum: Number(form.minimum),
          maximum: Number(form.maximum),
        },
        participantTypes: [
          {
            ...(project.participantTypes?.[0] || {}),
            role: form.role,
            additionalRequirements: form.additionalRequirements,
          },
        ],
        communicationLinks: {
          whatsappGroup: form.whatsappGroup,
          whatsappChannel: form.whatsappChannel,
        },
        visibility: form.visibility,
        ...(location ? { location } : {}),
      });
      setMessage(res.data?.message || "Project details updated successfully.");
      onUpdated?.();
    } catch (e) {
      setError(e.response?.data?.error || "Unable to submit project changes.");
    } finally {
      setSaving(false);
    }
  };

  const reviewRequest = async (participationId, status) => {
    try {
      await api.patch(
        `/participations/${project._id}/join-requests/${participationId}`,
        { status },
      );
      setRequests((c) => c.filter((r) => r._id !== participationId));
      setMessage(`Join request ${status}.`);
      if (status === "approved") {
        const res = await api.get(`/projects/${project._id}/participants`);
        setMembers(res.data || []);
      }
      onUpdated?.();
    } catch (e) {
      setError(e.response?.data?.error || "Unable to review join request.");
    }
  };

  const changeRole = async (member) => {
    const newRole = member.role === "leader" ? "member" : "leader";
    try {
      await api.patch(
        `/participations/${project._id}/members/${member._id}/role`,
        { role: newRole },
      );
      setMembers((c) =>
        c.map((m) => (m._id === member._id ? { ...m, role: newRole } : m)),
      );
      setMessage(
        newRole === "leader"
          ? `${member.student?.name || "Member"} is now a project leader. 👑`
          : `${member.student?.name || "Member"} has been moved to member.`,
      );
      onUpdated?.();
    } catch (e) {
      setError(e.response?.data?.error || "Unable to change the member role.");
    }
  };

  const loadFormResponses = async (selectedForm) => {
    setActiveResponseForm(selectedForm);
    setFormResponses([]);
    setResponsesError(null);
    setResponsesLoading(true);
    setResponseView("roster");
    try {
      const res = await api.get(
        `/projects/${project._id}/forms/${selectedForm._id}/responses`,
      );
      setFormResponses(res.data.responses || []);
    } catch (e) {
      setResponsesError(e.response?.data?.error || "Failed to load responses.");
    } finally {
      setResponsesLoading(false);
    }
  };

  const openAssignModal = (selectedForm) => {
    setAssignForm(selectedForm);
    setAssignRespondentId("");
    setAssignSlots({});
    setAssignError("");
    setAssignSuccess("");
    setShowAssignModal(true);
  };

  const toggleAssignSlot = (fieldId, slotId) => {
    setAssignSlots((prev) => {
      const cur = Array.isArray(prev[fieldId]) ? prev[fieldId] : [];
      return {
        ...prev,
        [fieldId]: cur.includes(slotId)
          ? cur.filter((id) => id !== slotId)
          : [...cur, slotId],
      };
    });
  };

  const handleAdminAssign = async (e) => {
    e.preventDefault();
    if (!assignRespondentId) {
      setAssignError("Please select a participant.");
      return;
    }
    setAssignSaving(true);
    setAssignError("");
    setAssignSuccess("");
    try {
      const answers = (assignForm.fields || [])
        .filter((f) => f.type !== "information")
        .map((f) => ({
          field: f._id,
          value: ["availability", "multi_choice"].includes(f.type)
            ? assignSlots[f._id] || []
            : assignSlots[f._id] || "",
        }));
      await api.post(
        `/projects/${project._id}/forms/${assignForm._id}/responses/admin-assign`,
        { respondentId: assignRespondentId, answers },
      );
      setAssignSuccess("Slots assigned successfully!");
      if (activeResponseForm?._id === assignForm._id)
        loadFormResponses(assignForm);
      await loadForms();
    } catch (e) {
      setAssignError(e.response?.data?.error || "Failed to assign response.");
    } finally {
      setAssignSaving(false);
    }
  };

  const leaderCount = members.filter((m) => m.role === "leader").length;

  const tabStyle = (id) => ({
    background: "none",
    border: "none",
    padding: "0.65rem 1.1rem",
    fontWeight: 600,
    fontSize: "0.88rem",
    cursor: "pointer",
    color:
      activeTab === id ? "var(--color-primary)" : "var(--color-text-secondary)",
    borderBottom:
      activeTab === id
        ? "3px solid var(--color-teal)"
        : "3px solid transparent",
    transition: "all 0.15s",
    whiteSpace: "nowrap",
  });

  /* ── Assign modal: build day-grouped slots across all availability fields ── */
  const buildDayGroupedSlots = (f) => {
    if (!f) return {};
    const all = f.fields
      .filter((fld) => fld.type === "availability")
      .flatMap((fld) =>
        (fld.slots || []).map((slot) => ({
          ...slot,
          fieldId: String(fld._id),
          fieldLabel: fld.label,
        })),
      );
    const byDate = {};
    for (const slot of all) {
      const key = new Date(slot.startTime).toLocaleDateString("en-ZA", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      });
      (byDate[key] ||= []).push(slot);
    }
    return byDate;
  };

  return (
    <>
      {/* ══ MAIN MODAL ════════════════════════════════════════════════════════ */}
      <Modal show={show} onHide={onHide} size="xl" centered scrollable>
        <Modal.Header
          closeButton
          style={{
            borderBottom: "2px solid var(--color-border)",
            flexShrink: 0,
          }}>
          <Modal.Title
            style={{ color: "var(--color-primary)", fontWeight: 700 }}>
            Manage — {project?.name}
          </Modal.Title>
        </Modal.Header>

        <div
          className="d-flex overflow-auto"
          style={{
            borderBottom: "2px solid var(--color-border)",
            backgroundColor: "var(--color-surface)",
            flexShrink: 0,
          }}>
          <button
            style={tabStyle(TAB_DETAILS)}
            onClick={() => setActiveTab(TAB_DETAILS)}>
            <FaEdit className="me-2" size={12} />
            Project Details
          </button>
          <button
            style={tabStyle(TAB_FORMS)}
            onClick={() => setActiveTab(TAB_FORMS)}>
            <FaWpforms className="me-2" size={12} />
            Forms &amp; Responses
            {forms.length > 0 && (
              <span
                className="ms-2"
                style={{
                  fontSize: "0.68rem",
                  padding: "1px 7px",
                  borderRadius: "20px",
                  backgroundColor: "rgba(0,172,150,0.15)",
                  color: "var(--color-teal)",
                  fontWeight: 700,
                }}>
                {forms.length}
              </span>
            )}
          </button>
          <button
            style={tabStyle(TAB_MEMBERS)}
            onClick={() => setActiveTab(TAB_MEMBERS)}>
            <FaUsers className="me-2" size={12} />
            Members
            {requests.length > 0 && (
              <span
                className="ms-2"
                style={{
                  fontSize: "0.68rem",
                  padding: "1px 7px",
                  borderRadius: "20px",
                  backgroundColor: "rgba(220,53,69,0.12)",
                  color: "#dc3545",
                  fontWeight: 700,
                }}>
                {requests.length} pending
              </span>
            )}
          </button>
        </div>

        <Modal.Body style={{ overflowY: "auto" }}>
          {message && (
            <Alert
              variant="success"
              dismissible
              onClose={() => setMessage(null)}>
              {message}
            </Alert>
          )}
          {error && (
            <Alert variant="danger" dismissible onClose={() => setError(null)}>
              {error}
            </Alert>
          )}

          {loading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
            </div>
          ) : (
            <>
              {/* ── DETAILS TAB ── */}
              {activeTab === TAB_DETAILS && (
                <>
                  <h6
                    className="fw-bold mb-1"
                    style={{ color: "var(--color-primary)" }}>
                    Project amendments
                  </h6>
                  <p className="text-muted small mb-3">
                    Changes to an approved project are submitted to an
                    administrator before becoming public.
                  </p>
                  <Form onSubmit={submitAmendment}>
                    <Form.Group className="mb-3">
                      <Form.Label>Description</Form.Label>
                      <Form.Control
                        as="textarea"
                        rows={3}
                        value={form.description || ""}
                        onChange={(e) =>
                          setForm({ ...form, description: e.target.value })
                        }
                        required
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Project Visibility</Form.Label>
                      <Form.Select
                        value={form.visibility || "public"}
                        onChange={(e) =>
                          setForm({ ...form, visibility: e.target.value })
                        }>
                        <option value="public">Public</option>
                        <option value="private">
                          Private (Requires approval)
                        </option>
                      </Form.Select>
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Volunteer role</Form.Label>
                      <Form.Control
                        value={form.role || ""}
                        onChange={(e) =>
                          setForm({ ...form, role: e.target.value })
                        }
                      />
                    </Form.Group>
                    <Form.Group className="mb-3">
                      <Form.Label>Additional requirements</Form.Label>
                      <Form.Control
                        as="textarea"
                        value={form.additionalRequirements || ""}
                        onChange={(e) =>
                          setForm({
                            ...form,
                            additionalRequirements: e.target.value,
                          })
                        }
                      />
                    </Form.Group>
                    <div className="d-flex gap-3 mb-3">
                      <Form.Group className="flex-grow-1">
                        <Form.Label>Minimum weekly hours</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          value={form.minimum ?? 0}
                          onChange={(e) =>
                            setForm({ ...form, minimum: e.target.value })
                          }
                        />
                      </Form.Group>
                      <Form.Group className="flex-grow-1">
                        <Form.Label>Maximum weekly hours</Form.Label>
                        <Form.Control
                          type="number"
                          min="0"
                          value={form.maximum ?? 0}
                          onChange={(e) =>
                            setForm({ ...form, maximum: e.target.value })
                          }
                        />
                      </Form.Group>
                    </div>
                    {project?.locationType !== "Online" && (
                      <>
                        <Form.Check
                          className="mb-2"
                          label="Transport provided"
                          checked={Boolean(form.transportProvided)}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              transportProvided: e.target.checked,
                            })
                          }
                        />
                        {form.transportProvided && (
                          <Form.Control
                            className="mb-3"
                            placeholder="Transport details"
                            value={form.transportDetails || ""}
                            onChange={(e) =>
                              setForm({
                                ...form,
                                transportDetails: e.target.value,
                              })
                            }
                          />
                        )}
                      </>
                    )}
                    <h6 className="mt-4">Project communication</h6>
                    <p className="text-muted small">
                      Optional links shown to students in the project details.
                    </p>
                    <div className="row g-3 mb-3">
                      <Form.Group className="col-md-6">
                        <Form.Label>
                          <FaWhatsapp className="me-2" />
                          WhatsApp group link
                        </Form.Label>
                        <Form.Control
                          type="url"
                          placeholder="https://chat.whatsapp.com/..."
                          value={form.whatsappGroup || ""}
                          onChange={(e) =>
                            setForm({ ...form, whatsappGroup: e.target.value })
                          }
                        />
                      </Form.Group>
                      <Form.Group className="col-md-6">
                        <Form.Label>
                          <FaWhatsapp className="me-2" />
                          WhatsApp channel link
                        </Form.Label>
                        <Form.Control
                          type="url"
                          placeholder="https://whatsapp.com/channel/..."
                          value={form.whatsappChannel || ""}
                          onChange={(e) =>
                            setForm({
                              ...form,
                              whatsappChannel: e.target.value,
                            })
                          }
                        />
                      </Form.Group>
                    </div>
                    <Button type="submit" disabled={saving}>
                      {saving ? "Submitting..." : "Submit changes"}
                    </Button>
                  </Form>
                </>
              )}

              {/* ── FORMS TAB ── */}
              {activeTab === TAB_FORMS && (
                <div>
                  <div className="d-flex align-items-center justify-content-between mb-4">
                    <div>
                      <h6
                        className="fw-bold mb-1"
                        style={{ color: "var(--color-primary)" }}>
                        Availability Forms
                      </h6>
                      <p className="text-muted small mb-0">
                        View who's available for each day and assign volunteers
                        to slots.
                      </p>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setShowFormBuilder(true)}
                      style={{
                        backgroundColor: "var(--color-primary)",
                        border: "none",
                        borderRadius: "var(--radius-md)",
                        fontWeight: 600,
                      }}>
                      <FaWpforms className="me-2" size={12} />
                      Create / Edit Forms
                    </Button>
                  </div>

                  {formsError && (
                    <Alert
                      variant="danger"
                      dismissible
                      onClose={() => setFormsError(null)}>
                      {formsError}
                    </Alert>
                  )}

                  {formsLoading ? (
                    <div className="text-center py-4">
                      <Spinner animation="border" variant="primary" />
                    </div>
                  ) : forms.length === 0 ? (
                    <div className="text-center py-5">
                      <FaWpforms
                        size={40}
                        className="mb-3"
                        style={{ color: "var(--color-border)" }}
                      />
                      <p className="text-muted mb-3">No forms created yet.</p>
                      <Button
                        size="sm"
                        onClick={() => setShowFormBuilder(true)}
                        style={{
                          backgroundColor: "var(--color-primary)",
                          border: "none",
                        }}>
                        Create First Form
                      </Button>
                    </div>
                  ) : (
                    <div className="d-flex flex-column gap-3 mb-4">
                      {forms.map((f) => {
                        const isPublished = f.status === "published";
                        const isDraft = f.status === "draft";
                        const statusColor = isPublished
                          ? "var(--color-teal)"
                          : isDraft
                            ? "var(--color-gold)"
                            : "var(--color-text-secondary)";
                        const statusBg = isPublished
                          ? "rgba(0,172,150,0.1)"
                          : isDraft
                            ? "rgba(230,176,0,0.1)"
                            : "rgba(0,0,0,0.05)";
                        return (
                          <div
                            key={f._id}
                            style={{
                              padding: "1rem 1.25rem",
                              borderRadius: "var(--radius-md)",
                              border: "1px solid var(--color-border)",
                              borderLeft: `4px solid ${statusColor}`,
                              backgroundColor: "var(--color-surface)",
                            }}>
                            <div className="d-flex align-items-start justify-content-between gap-2 mb-2">
                              <strong
                                style={{
                                  color: "var(--color-text)",
                                  fontSize: "0.95rem",
                                }}>
                                {f.title}
                              </strong>
                              <span
                                style={{
                                  fontSize: "0.68rem",
                                  padding: "2px 9px",
                                  borderRadius: "20px",
                                  backgroundColor: statusBg,
                                  color: statusColor,
                                  fontWeight: 700,
                                  whiteSpace: "nowrap",
                                }}>
                                {f.status?.toUpperCase()}
                              </span>
                            </div>
                            <div
                              className="d-flex flex-wrap gap-3 mb-2"
                              style={{
                                fontSize: "0.78rem",
                                color: "var(--color-text-secondary)",
                              }}>
                              <span>
                                📋 {f.responseCount ?? 0} response
                                {f.responseCount !== 1 ? "s" : ""}
                              </span>
                              {f.closesAt && (
                                <span>
                                  ⏰ Closes{" "}
                                  {formatSouthAfricaDateTime(
                                    f.closesAt,
                                    "dd MMM yyyy, HH:mm",
                                  )}{" "}
                                  {SOUTH_AFRICA_TIME_LABEL}
                                </span>
                              )}
                              <span>
                                📝 {f.fields?.length || 0} field
                                {f.fields?.length !== 1 ? "s" : ""}
                              </span>
                            </div>
                            <div className="d-flex gap-2 flex-wrap">
                              <Button
                                size="sm"
                                variant="outline-primary"
                                className="d-flex align-items-center gap-1"
                                onClick={() => loadFormResponses(f)}
                                style={{
                                  borderRadius: "var(--radius-sm)",
                                  fontSize: "0.78rem",
                                }}>
                                <FaCalendarDay size={10} />
                                View Availability
                              </Button>
                              <Button
                                size="sm"
                                className="d-flex align-items-center gap-1"
                                onClick={() => openAssignModal(f)}
                                style={{
                                  backgroundColor: "var(--color-gold)",
                                  border: "none",
                                  borderRadius: "var(--radius-sm)",
                                  fontSize: "0.78rem",
                                  color: "#fff",
                                }}>
                                <FaUserTag size={10} />
                                Assign to Slot
                              </Button>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {/* Response viewer */}
                  {activeResponseForm && (
                    <div
                      style={{
                        border: "1px solid var(--color-border)",
                        borderRadius: "var(--radius-md)",
                        overflow: "hidden",
                      }}>
                      <div
                        className="d-flex align-items-center justify-content-between p-3"
                        style={{
                          backgroundColor: "rgba(27,58,135,0.04)",
                          borderBottom: "1px solid var(--color-border)",
                          flexWrap: "wrap",
                          gap: "0.5rem",
                        }}>
                        <div>
                          <strong style={{ color: "var(--color-primary)" }}>
                            {responseView === "roster"
                              ? "📅 Availability Roster"
                              : "📊 All Responses"}{" "}
                            — {activeResponseForm.title}
                          </strong>
                          <div
                            className="text-muted"
                            style={{ fontSize: "0.78rem" }}>
                            {formResponses.length} response
                            {formResponses.length !== 1 ? "s" : ""} submitted
                          </div>
                        </div>
                        <div className="d-flex gap-2 align-items-center">
                          <div
                            className="btn-group btn-group-sm"
                            role="group"
                            aria-label="View mode">
                            <button
                              type="button"
                              onClick={() => setResponseView("roster")}
                              className={`btn btn-sm ${responseView === "roster" ? "btn-primary" : "btn-outline-secondary"}`}
                              style={{
                                fontSize: "0.72rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}>
                              <FaCalendarDay size={10} />
                              By Day
                            </button>
                            <button
                              type="button"
                              onClick={() => setResponseView("table")}
                              className={`btn btn-sm ${responseView === "table" ? "btn-primary" : "btn-outline-secondary"}`}
                              style={{
                                fontSize: "0.72rem",
                                display: "flex",
                                alignItems: "center",
                                gap: "4px",
                              }}>
                              <FaTable size={10} />
                              By Student
                            </button>
                          </div>
                          <Button
                            variant="outline-secondary"
                            size="sm"
                            onClick={() => setActiveResponseForm(null)}>
                            Close
                          </Button>
                        </div>
                      </div>

                      <div
                        style={{
                          padding: "1rem",
                          maxHeight: "55vh",
                          overflowY: "auto",
                        }}>
                        {responsesLoading ? (
                          <div className="text-center py-4">
                            <Spinner animation="border" size="sm" />
                          </div>
                        ) : responsesError ? (
                          <Alert variant="danger">{responsesError}</Alert>
                        ) : formResponses.length === 0 ? (
                          <Alert variant="info">
                            No responses submitted yet.
                          </Alert>
                        ) : responseView === "roster" ? (
                          <AvailabilityRoster
                            form={activeResponseForm}
                            responses={formResponses}
                          />
                        ) : (
                          <div style={{ overflowX: "auto" }}>
                            <Table
                              hover
                              className="mb-0"
                              style={{ fontSize: "0.82rem" }}>
                              <thead
                                style={{
                                  backgroundColor: "rgba(27,58,135,0.04)",
                                  borderBottom: "2px solid var(--color-border)",
                                }}>
                                <tr>
                                  <th
                                    className="py-2 ps-3"
                                    style={{
                                      color: "var(--color-primary)",
                                      fontWeight: 600,
                                    }}>
                                    Participant
                                  </th>
                                  {activeResponseForm.fields
                                    .filter((fld) => fld.type !== "information")
                                    .map((fld) => (
                                      <th
                                        key={fld._id}
                                        className="py-2"
                                        style={{
                                          color: "var(--color-primary)",
                                          fontWeight: 600,
                                          minWidth: "140px",
                                        }}>
                                        {fld.label}
                                      </th>
                                    ))}
                                  <th
                                    className="py-2 pe-3"
                                    style={{
                                      color: "var(--color-primary)",
                                      fontWeight: 600,
                                    }}>
                                    Submitted
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {formResponses.map((resp) => {
                                  const name = resp.respondent
                                    ? `${resp.respondent.name || ""} ${resp.respondent.surname || ""}`.trim() ||
                                      "Unknown"
                                    : "Unknown";
                                  return (
                                    <tr key={resp._id} className="align-middle">
                                      <td className="ps-3 fw-bold">
                                        {name}
                                        {resp.respondent?.studentNumber && (
                                          <div
                                            className="text-muted fw-normal"
                                            style={{ fontSize: "0.72rem" }}>
                                            {resp.respondent.studentNumber}
                                          </div>
                                        )}
                                      </td>
                                      {activeResponseForm.fields
                                        .filter(
                                          (fld) => fld.type !== "information",
                                        )
                                        .map((fld) => {
                                          const answer = resp.answers?.find(
                                            (a) =>
                                              String(a.field) ===
                                              String(fld._id),
                                          );
                                          let display = "—";
                                          if (answer) {
                                            if (fld.type === "availability") {
                                              const selected = Array.isArray(
                                                answer.value,
                                              )
                                                ? answer.value
                                                : [];
                                              const labels = selected.map(
                                                (slotId) => {
                                                  const slot = fld.slots?.find(
                                                    (s) =>
                                                      String(s._id) ===
                                                      String(slotId),
                                                  );
                                                  return slot
                                                    ? slot.label ||
                                                        formatSouthAfricaDateTime(
                                                          slot.startTime,
                                                        )
                                                    : slotId;
                                                },
                                              );
                                              display =
                                                labels.length > 0 ? (
                                                  <ul
                                                    className="mb-0 ps-3"
                                                    style={{
                                                      fontSize: "0.76rem",
                                                    }}>
                                                    {labels.map((lbl, i) => (
                                                      <li key={i}>{lbl}</li>
                                                    ))}
                                                  </ul>
                                                ) : (
                                                  <span className="text-muted">
                                                    None
                                                  </span>
                                                );
                                            } else if (
                                              Array.isArray(answer.value)
                                            ) {
                                              display =
                                                answer.value.join(", ") || "—";
                                            } else {
                                              display = answer.value || "—";
                                            }
                                          }
                                          return (
                                            <td key={fld._id}>{display}</td>
                                          );
                                        })}
                                      <td
                                        className="pe-3 text-muted"
                                        style={{ whiteSpace: "nowrap" }}>
                                        {new Date(
                                          resp.updatedAt,
                                        ).toLocaleString()}
                                      </td>
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </Table>
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* ── MEMBERS TAB ── */}
              {activeTab === TAB_MEMBERS && (
                <div>
                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6
                      className="fw-bold mb-0"
                      style={{ color: "var(--color-primary)" }}>
                      Pending Join Requests
                    </h6>
                    {requests.length > 0 && (
                      <span
                        style={{
                          fontSize: "0.72rem",
                          padding: "2px 9px",
                          borderRadius: "20px",
                          backgroundColor: "rgba(220,53,69,0.1)",
                          color: "#dc3545",
                          fontWeight: 700,
                        }}>
                        {requests.length} awaiting review
                      </span>
                    )}
                  </div>
                  {requests.length === 0 ? (
                    <p className="text-muted small mb-4">
                      No pending join requests.
                    </p>
                  ) : (
                    <ListGroup className="mb-4">
                      {requests.map((request) => (
                        <ListGroup.Item
                          key={request._id}
                          className="d-flex justify-content-between align-items-center py-3">
                          <span>
                            <strong>
                              {request.student?.name} {request.student?.surname}
                            </strong>
                            <small className="text-muted ms-2">
                              {request.student?.studentNumber}
                            </small>
                          </span>
                          <span className="d-flex gap-2">
                            <Button
                              size="sm"
                              variant="success"
                              onClick={() =>
                                reviewRequest(request._id, "approved")
                              }>
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              variant="outline-danger"
                              onClick={() =>
                                reviewRequest(request._id, "rejected")
                              }>
                              Reject
                            </Button>
                          </span>
                        </ListGroup.Item>
                      ))}
                    </ListGroup>
                  )}

                  <div className="d-flex align-items-center justify-content-between mb-2">
                    <h6
                      className="fw-bold mb-0"
                      style={{ color: "var(--color-primary)" }}>
                      Leaders &amp; Members
                    </h6>
                    <span
                      className="text-muted"
                      style={{ fontSize: "0.78rem" }}>
                      {leaderCount} leader{leaderCount !== 1 ? "s" : ""} ·{" "}
                      {members.length - leaderCount} member
                      {members.length - leaderCount !== 1 ? "s" : ""}
                    </span>
                  </div>
                  <p className="text-muted small mb-3">
                    Promote members to project leader or demote existing
                    leaders. A project must always have at least one leader.
                  </p>
                  <ListGroup>
                    {members.map((member) => {
                      const isLeader = member.role === "leader";
                      const name =
                        member.student?.organizationName ||
                        `${member.student?.name || ""} ${member.student?.surname || ""}`.trim() ||
                        "Unknown";
                      return (
                        <ListGroup.Item
                          key={member._id}
                          className="d-flex justify-content-between align-items-center py-3"
                          style={{
                            borderLeft: isLeader
                              ? "4px solid var(--color-gold)"
                              : "4px solid transparent",
                          }}>
                          <span className="d-flex align-items-center gap-2">
                            <div
                              className="rounded-circle d-flex align-items-center justify-content-center fw-bold text-white"
                              style={{
                                width: "36px",
                                height: "36px",
                                backgroundColor: isLeader
                                  ? "var(--color-gold)"
                                  : "var(--color-primary)",
                                fontSize: "0.85rem",
                                flexShrink: 0,
                              }}>
                              {name.charAt(0).toUpperCase()}
                            </div>
                            <div>
                              <div
                                className="fw-bold"
                                style={{ fontSize: "0.9rem" }}>
                                {name}
                              </div>
                              <div
                                style={{
                                  fontSize: "0.75rem",
                                  color: isLeader
                                    ? "var(--color-gold)"
                                    : "var(--color-text-secondary)",
                                  fontWeight: 600,
                                }}>
                                {isLeader ? (
                                  <>
                                    <FaUserShield size={10} className="me-1" />
                                    Leader
                                  </>
                                ) : (
                                  "Member"
                                )}
                              </div>
                            </div>
                          </span>
                          <Button
                            size="sm"
                            variant={
                              isLeader ? "outline-secondary" : "outline-warning"
                            }
                            className="d-flex align-items-center gap-1"
                            onClick={() => changeRole(member)}
                            style={{
                              fontSize: "0.78rem",
                              borderRadius: "var(--radius-sm)",
                            }}>
                            {isLeader ? (
                              <>
                                <FaUserMinus size={11} />
                                Demote
                              </>
                            ) : (
                              <>
                                <FaUserShield size={11} />
                                Make Leader
                              </>
                            )}
                          </Button>
                        </ListGroup.Item>
                      );
                    })}
                  </ListGroup>
                </div>
              )}
            </>
          )}
        </Modal.Body>
      </Modal>

      {/* ══ FORM BUILDER SUB-MODAL ════════════════════════════════════════════ */}
      <Modal
        show={showFormBuilder}
        onHide={() => setShowFormBuilder(false)}
        size="xl"
        centered
        scrollable>
        <Modal.Header
          closeButton
          style={{ borderBottom: "2px solid var(--color-border)" }}>
          <Modal.Title
            style={{ color: "var(--color-primary)", fontWeight: 700 }}>
            <FaWpforms className="me-2" />
            Forms Manager — {project?.name}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ overflowY: "auto", padding: "1.5rem" }}>
          <ProjectFormManager projectId={project?._id} />
        </Modal.Body>
        <Modal.Footer style={{ borderTop: "1px solid var(--color-border)" }}>
          <Button
            variant="outline-secondary"
            onClick={() => {
              setShowFormBuilder(false);
              loadForms();
            }}>
            Done
          </Button>
        </Modal.Footer>
      </Modal>

      {/* ══ ASSIGN-TO-SLOT SUB-MODAL ══════════════════════════════════════════ */}
      <Modal
        show={showAssignModal}
        onHide={() => !assignSaving && setShowAssignModal(false)}
        centered
        size="lg"
        scrollable
        dialogClassName="assign-slot-modal-dialog">
        <Modal.Header
          closeButton
          style={{ borderBottom: "2px solid var(--color-border)" }}>
          <Modal.Title
            style={{ color: "var(--color-primary)", fontWeight: 700 }}>
            <FaUserTag className="me-2" />
            Assign Participant to Slot
          </Modal.Title>
        </Modal.Header>
        <Form
          onSubmit={handleAdminAssign}
          style={{
            display: "flex",
            flexDirection: "column",
            flex: "1 1 auto",
            minHeight: 0,
            margin: 0,
          }}>
          <Modal.Body
            style={{ overflowY: "auto", flex: "1 1 auto", minHeight: 0 }}>
            {assignError && <Alert variant="danger">{assignError}</Alert>}
            {assignSuccess && <Alert variant="success">{assignSuccess}</Alert>}
            {assignForm && (
              <>
                <p className="text-muted small mb-1">Form</p>
                <p className="fw-bold mb-4">{assignForm.title}</p>

                <Form.Group className="mb-4">
                  <Form.Label
                    style={{ fontWeight: 600, color: "var(--color-primary)" }}>
                    Select Participant
                  </Form.Label>
                  <Form.Select
                    value={assignRespondentId}
                    onChange={(e) => setAssignRespondentId(e.target.value)}
                    required
                    style={{
                      borderRadius: "var(--radius-md)",
                      borderColor: "var(--color-border)",
                    }}>
                    <option value="">— Choose a participant —</option>
                    {members.map((m) => (
                      <option key={m._id} value={m.student?._id || m.student}>
                        {m.student?.name || "Unknown"}{" "}
                        {m.student?.surname || ""}
                        {m.student?.studentNumber
                          ? ` (${m.student.studentNumber})`
                          : ""}
                        {m.role === "leader" ? " 👑" : ""}
                      </option>
                    ))}
                  </Form.Select>
                </Form.Group>

                {/* Slots grouped by date */}
                {(() => {
                  const byDate = buildDayGroupedSlots(assignForm);
                  const days = Object.keys(byDate).sort(
                    (a, b) =>
                      new Date(byDate[a][0].startTime) -
                      new Date(byDate[b][0].startTime),
                  );
                  if (days.length === 0)
                    return (
                      <Alert variant="info" style={{ fontSize: "0.85rem" }}>
                        This form has no availability slots.
                      </Alert>
                    );
                  return (
                    <div className="d-flex flex-column gap-4">
                      {days.map((day) => (
                        <div key={day}>
                          <div
                            className="d-flex align-items-center gap-2 mb-2 pb-1"
                            style={{
                              borderBottom: "2px solid var(--color-border)",
                            }}>
                            <FaCalendarDay
                              size={12}
                              style={{ color: "var(--color-primary)" }}
                            />
                            <strong
                              style={{
                                color: "var(--color-primary)",
                                fontSize: "0.88rem",
                              }}>
                              {day}
                            </strong>
                          </div>
                          <div className="d-flex flex-column gap-2">
                            {byDate[day].map((slot) => {
                              const isSelected = (
                                assignSlots[slot.fieldId] || []
                              ).includes(String(slot._id));
                              return (
                                <label
                                  key={slot._id}
                                  style={{
                                    display: "flex",
                                    alignItems: "flex-start",
                                    gap: "0.6rem",
                                    padding: "0.7rem 1rem",
                                    borderRadius: "var(--radius-md)",
                                    border: `2px solid ${isSelected ? "var(--color-teal)" : "var(--color-border)"}`,
                                    backgroundColor: isSelected
                                      ? "rgba(0,172,150,0.07)"
                                      : "var(--color-surface)",
                                    cursor: "pointer",
                                    transition: "all 0.12s",
                                  }}>
                                  <input
                                    type="checkbox"
                                    checked={isSelected}
                                    onChange={() =>
                                      toggleAssignSlot(
                                        slot.fieldId,
                                        String(slot._id),
                                      )
                                    }
                                    style={{
                                      marginTop: "3px",
                                      accentColor: "var(--color-teal)",
                                    }}
                                  />
                                  <span>
                                    <strong
                                      style={{
                                        display: "block",
                                        fontSize: "0.88rem",
                                        color: "var(--color-text)",
                                      }}>
                                      {slot.label || slot.activity}
                                    </strong>
                                    <small
                                      style={{
                                        color: "var(--color-text-secondary)",
                                      }}>
                                      {formatSouthAfricaDateTime(
                                        slot.startTime,
                                        "HH:mm",
                                      )}{" "}
                                      –{" "}
                                      {formatSouthAfricaDateTime(
                                        slot.endTime,
                                        "HH:mm",
                                      )}{" "}
                                      {SOUTH_AFRICA_TIME_LABEL}
                                      {slot.location
                                        ? ` · ${slot.location}`
                                        : ""}
                                    </small>
                                    {slot.capacity > 0 && (
                                      <small
                                        style={{
                                          display: "block",
                                          color: "var(--color-text-secondary)",
                                        }}>
                                        Capacity: {slot.capacity}
                                      </small>
                                    )}
                                  </span>
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </>
            )}
          </Modal.Body>
          <Modal.Footer style={{ borderTop: "1px solid var(--color-border)" }}>
            <Button
              variant="outline-secondary"
              onClick={() => setShowAssignModal(false)}
              disabled={assignSaving}>
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={assignSaving || !assignRespondentId}
              style={{
                backgroundColor: "var(--color-teal)",
                border: "none",
                fontWeight: 600,
              }}>
              {assignSaving ? (
                <>
                  <Spinner animation="border" size="sm" className="me-2" />
                  Saving…
                </>
              ) : (
                <>
                  <FaCheck className="me-2" />
                  Assign Slots
                </>
              )}
            </Button>
          </Modal.Footer>
        </Form>
      </Modal>
    </>
  );
};

export default ProjectLeaderManagementModal;
